import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Btn } from '../components/Btn.tsx';
import { ExpiryChip } from '../components/ExpiryChip.tsx';
import { NavIcon } from '../components/NavIcon.tsx';
import { SubmissionStatusBadge } from '../components/StatusBadge.tsx';
import { expiryState } from '../lib/expiry.ts';
import { effectiveDocStatus } from '../lib/submissionStatus.ts';
import { instanceOptionLabel } from '../lib/instanceOption.ts';
import { complianceDocName } from '../lib/complianceUploads.ts';
import type { ComplianceDocRow } from '../lib/companyDetailTabs.ts';
import type { AdminSubmission, CompanyDocument, Question } from '../lib/types.ts';
import { awaitingAdminReply } from './awaitingReply.ts';
import { CommentModal } from './CommentModal.tsx';
import { documentRowData } from './documentRow.ts';
import { FileHistoryModal } from './FileHistoryModal.tsx';
import { FilePreviewModal } from './FilePreviewModal.tsx';
import { NotCompliantModal } from './NotCompliantModal.tsx';
import { useReviewMutation } from './useReviewMutation.ts';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// A scannable table of document_upload questions (the Compliance Documents
// tab). This table is verify-only for approved docs: it lists them and their
// status, with Compliant / Not compliant / N/A happening inline in the
// questionnaire tabs (SubmissionRow) instead. The exception is renewal rows
// awaiting re-verification (vendor replaced an expired approved document),
// which carry the same three review buttons as the questionnaire rows. Other
// question types keep the SubmissionRow layout (handled by the caller).
export function DocumentReviewTable({
  rows,
  subByQ,
  companyId,
  documents,
  documentName,
  questionById,
}: {
  rows: ComplianceDocRow[];
  subByQ: Map<string, AdminSubmission>;
  companyId: string;
  documents: CompanyDocument[];
  documentName: (id: string) => string;
  questionById: Map<string, Question>;
}) {
  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2">Document</th>
            <th className="px-4 py-2">Category</th>
            <th className="px-4 py-2">Uploaded</th>
            <th className="px-4 py-2">Expiry</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ question, categoryName }) => (
            <DocumentReviewRow
              key={question.id}
              question={question}
              categoryName={categoryName}
              submission={subByQ.get(question.id)}
              companyId={companyId}
              documents={documents}
              documentName={documentName}
              instanceOption={instanceOptionLabel(question, questionById)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DocumentReviewRow({
  question,
  categoryName,
  submission,
  companyId,
  documents,
  documentName,
  instanceOption,
}: {
  question: Question;
  categoryName: string;
  submission: AdminSubmission | undefined;
  companyId: string;
  documents: CompanyDocument[];
  documentName: (id: string) => string;
  instanceOption: string | null;
}) {
  const docName = complianceDocName(question);
  const [commentOpen, setCommentOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const reviewMutation = useReviewMutation(companyId, submission?.id, () => setRejectOpen(false));
  // Renewal awaiting re-verification: the vendor replaced an expired approved
  // document. Only these rows carry review buttons — approved rows stay
  // verify-only (PR #149). The admin endpoint returns the effective (drafted)
  // status, so the buttons disappear as soon as a verdict is drafted.
  const needsReverify =
    !!submission &&
    submission.reverify_requested_at != null &&
    submission.status !== 'approved' &&
    submission.status !== 'not_applicable';

  const row = documentRowData(submission);
  const files = submission?.submission_files ?? [];
  const awaitingReply = awaitingAdminReply(submission);
  // An approved document whose expiry has lapsed is no longer compliant — show
  // it as "Not Compliant" (display-only; see effectiveDocStatus).
  const expired = row.kind === 'file' && expiryState({ expires_at: row.expiresAt }) === 'expired';

  const uploaded =
    row.kind === 'file'
      ? formatDate(row.uploadedAt)
      : row.kind === 'adopted' && row.uploadedAt
        ? formatDate(row.uploadedAt)
        : '—';

  return (
    <>
      <tr className="border-t border-slate-100 align-top">
        <td className="px-4 py-3">
          <div className="font-semibold text-slate-900">
            {docName}
            {instanceOption && (
              <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-normal text-indigo-700">
                For: {instanceOption}
              </span>
            )}
          </div>
          {submission?.reviewer_comment && (
            <div
              className="mt-1 flex items-start gap-1 text-xs text-orange-700"
              title={submission.reviewer_comment}
            >
              <NavIcon name="message-square" size={12} />
              <span>{submission.reviewer_comment}</span>
            </div>
          )}
          {question.required && (
            <span className="mt-0.5 inline-block rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-600">
              Mandatory
            </span>
          )}
          {row.kind === 'adopted' && (
            <div className="mt-0.5 text-xs text-slate-500">
              Adopted: {row.documentId ? documentName(row.documentId) : 'document'}
            </div>
          )}
          {row.kind === 'awaiting' && (
            <div className="mt-0.5 text-xs italic text-slate-400">Awaiting document</div>
          )}
        </td>
        <td className="px-4 py-3 text-slate-600">{categoryName}</td>
        <td className="px-4 py-3 text-slate-600">{uploaded}</td>
        <td className="px-4 py-3">
          {row.kind === 'file' && row.expiresAt ? (
            <ExpiryChip expiresAt={row.expiresAt} />
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
        <td className="px-4 py-3">
          <SubmissionStatusBadge status={effectiveDocStatus(submission?.status ?? 'pending', expired)} />
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center justify-end gap-1">
            {needsReverify && submission && (
              <>
                <Btn
                  size="sm"
                  variant="secondary"
                  aria-label={`Mark "${docName}" compliant`}
                  onClick={() => reviewMutation.mutate({ decision: 'approve' })}
                  loading={reviewMutation.isPending && reviewMutation.variables?.decision === 'approve'}
                >
                  <NavIcon name="check-circle" size={16} />
                  Compliant
                </Btn>
                <Btn
                  size="sm"
                  variant={submission.status === 'rejected' ? 'danger' : 'secondary'}
                  aria-label={`Mark "${docName}" not compliant`}
                  onClick={() => setRejectOpen(true)}
                  loading={reviewMutation.isPending && reviewMutation.variables?.decision === 'reject'}
                >
                  <NavIcon name="x-circle" size={16} />
                  Not Compliant
                </Btn>
                <Btn
                  size="sm"
                  variant="secondary"
                  aria-label={`Mark "${docName}" not applicable`}
                  onClick={() => reviewMutation.mutate({ decision: 'na' })}
                  loading={reviewMutation.isPending && reviewMutation.variables?.decision === 'na'}
                >
                  N/A
                </Btn>
              </>
            )}
            <Btn
              size="sm"
              variant="secondary"
              aria-label="Document history"
              title="History"
              disabled={!submission}
              onClick={() => setHistoryOpen(true)}
            >
              <NavIcon name="clock" size={16} />
            </Btn>
            <Btn
              size="sm"
              variant="secondary"
              aria-label="View document"
              title="View"
              disabled={row.kind !== 'file'}
              onClick={() => setPreviewOpen(true)}
            >
              <NavIcon name="eye" size={16} />
            </Btn>
            {question.exclude_from_compliance ? (
              <span className="ml-1 text-xs text-slate-500">
                Not part of compliance — no review needed.
              </span>
            ) : (
              <span className="relative inline-flex">
                <Btn
                  size="sm"
                  variant="secondary"
                  aria-label={
                    awaitingReply
                      ? `Vendor replied — awaiting your reply for "${docName}"`
                      : `Add comment for "${docName}"`
                  }
                  title={awaitingReply ? 'Vendor replied — awaiting your reply' : 'Comment'}
                  onClick={() => setCommentOpen(true)}
                >
                  <NavIcon name="message-square" size={16} />
                </Btn>
                {awaitingReply && (
                  <span
                    className="pointer-events-none absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white"
                    aria-hidden="true"
                  />
                )}
              </span>
            )}
          </div>
        </td>
      </tr>
      {createPortal(
        <>
          {commentOpen && (
            <CommentModal
              question={question}
              companyId={companyId}
              submissionId={submission?.id}
              onClose={() => setCommentOpen(false)}
            />
          )}
          {previewOpen && row.kind === 'file' && submission && (
            <FilePreviewModal
              companyId={companyId}
              submissionId={submission.id}
              files={files}
              startIndex={Math.max(0, files.indexOf(row.primaryFile))}
              onClose={() => setPreviewOpen(false)}
            />
          )}
          {historyOpen && submission && (
            <FileHistoryModal
              companyId={companyId}
              submissionId={submission.id}
              questionLabel={docName}
              onClose={() => setHistoryOpen(false)}
            />
          )}
          {rejectOpen && (
            <NotCompliantModal
              question={question}
              displayName={docName}
              documents={documents}
              pending={reviewMutation.isPending}
              error={reviewMutation.error}
              onClose={() => setRejectOpen(false)}
              onSubmit={(payload) => reviewMutation.mutate({ decision: 'reject', ...payload })}
            />
          )}
        </>,
        document.body,
      )}
    </>
  );
}
